import {
  collection,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../platform/firebase/clients';

// Mirror of the writer-side shape in functions/services/llmUsage.js. The
// frontend only reads (admins, via the firestore.rules `llm_usage` allow-read)
// — the doc is append-only so a snapshot/get-once pattern is sufficient for
// the dashboard.
export interface LlmUsageServerToolUse {
  webSearchRequests: number;
  webFetchRequests: number;
  codeExecutionRequests: number;
}

export interface LlmUsageBreakdown {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  serverToolUse: LlmUsageServerToolUse;
}

export interface LlmUsageRecord {
  id: string;
  createdAt: Date | null;
  userId: string | null;
  userEmail: string | null;
  taskId: string | null;
  taskType: string | null;
  campaignId: string | null;
  campaignName: string | null;
  provider: string;
  model: string;
  pricingId: string;
  rateVersion: string;
  usage: LlmUsageBreakdown;
  costMicroUsd: number;
  durationMs: number | null;
  status: string;
  errorMessage: string | null;
}

function toDateOrNull(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function mapServerToolUse(raw: unknown): LlmUsageServerToolUse {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    webSearchRequests: Number(r.webSearchRequests) || 0,
    webFetchRequests: Number(r.webFetchRequests) || 0,
    codeExecutionRequests: Number(r.codeExecutionRequests) || 0,
  };
}

function mapUsage(raw: unknown): LlmUsageBreakdown {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    inputTokens: Number(r.inputTokens) || 0,
    outputTokens: Number(r.outputTokens) || 0,
    cacheReadTokens: Number(r.cacheReadTokens) || 0,
    cacheWriteTokens: Number(r.cacheWriteTokens) || 0,
    serverToolUse: mapServerToolUse(r.serverToolUse),
  };
}

function mapRecord(id: string, data: Record<string, unknown>): LlmUsageRecord {
  return {
    id,
    createdAt: toDateOrNull(data.createdAt),
    userId: (data.userId as string) ?? null,
    userEmail: (data.userEmail as string) ?? null,
    taskId: (data.taskId as string) ?? null,
    taskType: (data.taskType as string) ?? null,
    campaignId: (data.campaignId as string) ?? null,
    campaignName: (data.campaignName as string) ?? null,
    provider: typeof data.provider === 'string' ? data.provider : 'unknown',
    model: typeof data.model === 'string' ? data.model : 'unknown',
    pricingId: typeof data.pricingId === 'string' ? data.pricingId : '',
    rateVersion: typeof data.rateVersion === 'string' ? data.rateVersion : '',
    usage: mapUsage(data.usage),
    costMicroUsd: Number(data.costMicroUsd) || 0,
    durationMs: typeof data.durationMs === 'number' ? data.durationMs : null,
    status: typeof data.status === 'string' ? data.status : '',
    errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : null,
  };
}

export interface FetchLlmUsageOptions {
  /** Filter to a single campaign. Omit to read across all campaigns. */
  campaignId?: string | null;
  /** Inclusive lower bound on `createdAt`. Omit for no lower bound. */
  since?: Date | null;
  /** Cap on the number of docs returned. Defaults to 5000. */
  limit?: number;
}

const DEFAULT_LIMIT = 5000;

/**
 * One-shot fetch of llm_usage records. Used by the admin token dashboard.
 *
 * llm_usage is append-only, so a get-once read returns a complete view of the
 * window the caller asked for — no need for a live subscription. Costs are
 * already computed and stamped on each row by the writer.
 *
 * Server-side filtering uses the (campaignId, createdAt) and (createdAt)
 * composite indexes already in `firestore.indexes.json`. The since-bound
 * piggybacks on the same orderBy(createdAt) so no extra index is needed.
 */
export async function fetchLlmUsage(opts: FetchLlmUsageOptions = {}): Promise<LlmUsageRecord[]> {
  const { campaignId, since, limit = DEFAULT_LIMIT } = opts;
  const constraints = [];
  if (campaignId) constraints.push(where('campaignId', '==', campaignId));
  if (since) constraints.push(where('createdAt', '>=', Timestamp.fromDate(since)));
  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limitFn(limit));
  const q = query(collection(db, 'llm_usage'), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapRecord(d.id, d.data() as Record<string, unknown>));
}
